import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { useSaveServiceRoles } from '../../../../hooks/queries/privateBankingServiceRoles';
import { setSelectedPBProduct } from '../../../../reducers/privateBankingReducer';
import { closeModal } from '../../../../reducers/modalReducer';
import { showToast } from '../../../../reducers/toastReducer';
import { useErrorModal } from '../../../../hooks/useErrorModal';
import { getErrorDetailsForCause } from '../utils/saveUtils';

type SaveAndNotifyRequest = {
  serviceAgreementUid: string | undefined;
  partnerIds: string[];
  relatedServiceAgreementUids?: string[];
  contactNoteUids?: string[];
  successToastTitle: string;
  closeBeforeErrorModal?: boolean;
};

type ShowSaveErrorModalOptions = {
  closeBeforeErrorModal?: boolean;
};

export const useSaveServiceRolesAndNotify = () => {
  const dispatch = useDispatch();
  const { mutateAsync: saveServiceRoles, isPending } = useSaveServiceRoles();
  const { showErrorModal } = useErrorModal();

  const showSaveErrorModal = useCallback(
    async (error: unknown, { closeBeforeErrorModal = false }: ShowSaveErrorModalOptions = {}) => {
      const details = await getErrorDetailsForCause(error);

      if (closeBeforeErrorModal) {
        dispatch(closeModal());
      }

      showErrorModal(error, {
        title: 'Error when saving service roles',
        userErrorMessage: 'An error occurred when saving service roles. Please try again later.',
        details,
      });
    },
    [dispatch, showErrorModal],
  );

  const saveAndNotify = useCallback(
    async ({
      serviceAgreementUid,
      partnerIds,
      relatedServiceAgreementUids = [],
      contactNoteUids,
      successToastTitle,
      closeBeforeErrorModal = false,
    }: SaveAndNotifyRequest): Promise<ServiceAgreementV2 | undefined> => {
      if (!serviceAgreementUid) {
        await showSaveErrorModal(new Error('serviceAgreementId is required'), { closeBeforeErrorModal });
        return undefined;
      }

      try {
        const updatedServiceAgreement = await saveServiceRoles({
          serviceAgreementUid,
          partnerIds,
          relatedServiceAgreementUids,
          contactNoteUids,
        });

        dispatch(setSelectedPBProduct(updatedServiceAgreement));
        dispatch(closeModal());
        dispatch(
          showToast({
            type: 'success',
            position: 'top-center',
            titleText: successToastTitle,
            dismissible: true,
            autoDismissDuration: 5000,
          }),
        );

        return updatedServiceAgreement;
      } catch (error) {
        await showSaveErrorModal(error, { closeBeforeErrorModal });
        return undefined;
      }
    },
    [dispatch, saveServiceRoles, showSaveErrorModal],
  );

  return {
    isPending,
    saveAndNotify,
    showSaveErrorModal,
  };
};
